/**
 * 매물 필드를 동적으로 생성하는 유틸리티
 */

// 시스템 필드 (제외할 필드)
const SYSTEM_FIELDS = ['id', 'name', 'type', 'price', 'createdAt', 'updatedAt'];

// 숫자 필드 (만원 단위)
const NUMBER_FIELDS = ['lat', 'lng'];

// 특수 필드 설정
const FIELD_CONFIG = {
  '파일명': { type: 'text', placeholder: '예: 210310 물건카드...' },
  '구분': { type: 'select', options: ['상가 임대', '상가 매매', '사무실 임대', '사무실 매매'], default: '상가 임대', hidden: true },
  '상호': { required: true, type: 'text' },
  '층수(해당/전체)': { type: 'text' },
  '평수': { type: 'text' },
  '관계인': { type: 'text' },
  '연락처': { type: 'tel' },
  '보증금': { type: 'text' },
  '월세': { type: 'text' },
  '관리비': { type: 'text' },
  '권리금': { type: 'text', nullable: true },
  '현업종': { type: 'text' },
  '용도': { type: 'text' },
  '화장실': { type: 'text' },
  '주차': { type: 'text' },
  '사용승인일': { type: 'date', label: '입점 가능일' },
  '접수일자': { type: 'date' },
  '담당자': { type: 'text' },
  '특이사항': { type: 'textarea' },
  'lat': { type: 'number', hidden: true }, // 주소로 자동 변환되므로 숨김
  'lng': { type: 'number', hidden: true }, // 주소로 자동 변환되므로 숨김
  // 기존 필드 호환용
  '현층/총층수': { type: 'text' },
  '엘베': { type: 'select', options: ['유', '무'], default: '무' },
  '상권': { type: 'text' },
  '담당': { type: 'text', hidden: true } // 담당자와 중복되어 숨김 처리
};

export const BASE_PROPERTY_FIELDS = [
  '상권', '상호', '주소', '층수(해당/전체)', '평수',
  '보증금', '월세', '관리비', '권리금',
  '현업종', '용도', '화장실', '엘베', '주차',
  '사용승인일', '접수일자', '담당자',
  '관계인', '연락처', '특이사항', '파일명'
];

/**
 * 기존 매물 데이터로부터 모든 필드를 추출하여 필드 목록 생성
 * @param {Array} properties - 기존 매물 데이터 배열
 * @returns {Array} 필드 정의 배열
 */
export function generatePropertyFields(properties = []) {

  // 모든 매물의 키를 수집
  const dataKeys = new Set();
  
  properties.forEach(property => {
    if (property && typeof property === 'object') {
      Object.keys(property).forEach(key => {
        if (!SYSTEM_FIELDS.includes(key) && property[key] !== undefined) {
          dataKeys.add(key);
        }
      });
    }
  });

  const orderedDefaults = BASE_PROPERTY_FIELDS;

  const additionalFields = Array.from(dataKeys).filter(
    (key) => !BASE_PROPERTY_FIELDS.includes(key)
  );

  const allFields = [...orderedDefaults, ...additionalFields];

  // 필드 정의 생성
  return allFields.map(key => {
    const config = FIELD_CONFIG[key] || {};
    
    // 필드 타입 결정
    let type = config.type || 'text';
    if (!config.type) {
      if (NUMBER_FIELDS.includes(key)) {
        type = 'number';
      } else if (typeof properties[0]?.[key] === 'number') {
        type = 'number';
      } else {
        type = 'text';
      }
    }

    return {
      key,
      label: config.label || key,
      type,
      required: config.required || false,
      placeholder: getPlaceholder(key),
      unit: config.unit || (NUMBER_FIELDS.includes(key) ? '만원' : undefined),
      nullable: config.nullable || false,
      options: config.options,
      default: config.default,
      hidden: config.hidden || false
    };
  }).filter(field => !field.hidden); // 숨김 필드 제외
}

/**
 * 필드별 placeholder 생성
 */
function getPlaceholder(key) {
  const placeholders = {
    '파일명': '예: 210310 물건카드.pdf',
    '층수(해당/전체)': '예: 1층 / 3층',
    '평수': '예: 30평, 층당 15평',
    '관계인': '예: 건물주, 임차인, 점주',
    '연락처': '숫자만 입력',
    '보증금': '예: 5000, 별도문의',
    '월세': '예: 200(부.별), 별도문의',
    '관리비': '예: 무, 10',
    '권리금': '예: 무, 2억',
    '현업종': '예: 공실, 술집',
    '화장실': '예: 내부, 외부공용, 복도',
    '주차': '예: 무, 2대, 건물 앞',
    '주소': '주소를 입력하면 자동으로 좌표가 설정됩니다',
    '사용승인일': '입점 가능일을 선택하세요',
    '접수일자': '접수일자를 선택하세요',
    '특이사항': '추가로 남길 메모를 입력하세요'
  };
  return placeholders[key];
}

/**
 * 테이블 컬럼 정의 생성 (포맷팅 포함)
 */
export function generatePropertyColumns(properties = []) {
  const fields = generatePropertyFields(properties);
  
  // 층수 필드 중복 제거: '현층/총층수'는 제외하고 '층수(해당/전체)'만 사용
  const filteredFields = fields.filter(field => field.key !== '현층/총층수');
  
  return filteredFields.map(field => {
    const column = {
      key: field.key,
      label: field.label
    };

    // 숫자 필드 포맷팅
    if (NUMBER_FIELDS.includes(field.key)) {
      if (field.key === '권리금') {
        column.format = (value) => value === null || value === undefined ? '무권리' : `${value}만원`;
      } else {
        column.format = (value) => value ? `${value}만원` : '-';
      }
    }

    return column;
  });
}

